import React, { useState } from "react";
import { BadgeCheck, Lock, Eye, EyeOff, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { clearCache, getCache, setCache } from "../../util/Cache";
// import Logo from "../../controller/Logo";
import logo from '../../assets/image/logo512.png';
import type { Karyawan } from "../../controller/mobile/types";
import { post_user_login } from "../../controller/Login";
import { useNavigate } from "react-router-dom";

async function mockLogin(noKary: string, password: string) {
  const nama_apps = process.env.REACT_APP_NAMA_APPS || "bawaslu_phwt";
  
  const formdata = new FormData();
  formdata.append("username_email", noKary);
  formdata.append("password", password);
  formdata.append("nama_apps", nama_apps);

  const result = await post_user_login(formdata);
  return result;
}


type ViewState = "form" | "success";
type SubmitState = "idle" | "loading" | "error";

export default function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewState>("form");
  const [loggedInEmployee, setLoggedInEmployee] = useState<Karyawan | null>(
    null
  );
 
  const [noKary, setNoKary] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
 
  const canSubmit =
    noKary.trim().length > 0 && password.length > 0 && submitState !== "loading";
 
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
 
    setSubmitState("loading");
    setErrorMessage(null);
    try {
      const result = await mockLogin(noKary, password);

      if (String(result?.status) === '200' && result?.data) {
        setSubmitState("idle");
        setLoggedInEmployee(result.data);
        setView("success");
        setCache("current_user", result.data);
        setTimeout(() => navigate("/apps/mobile"), 1000);
      } else {
        setSubmitState("error");
        setErrorMessage(String(result?.message ? "Terjadi kesalahan saat login." : "Gagal masuk. Silakan coba lagi." ));
      }
    } catch (err: any) {
      console.error('Login error', err);
      setSubmitState('error');
      setErrorMessage(String(err?.message ?? 'Terjadi kesalahan jaringan. Coba lagi.'));
    }
  };
 
  return (
    <div className="min-h-screen w-full bg-slate-200 flex items-center justify-center sm:p-6">
      {/* Phone frame: full-bleed on mobile, native-app-like frame on desktop */}
      <div className="flex h-screen w-full max-w-sm flex-col overflow-hidden bg-white sm:h-[812px] sm:rounded-[2.5rem] sm:border-8 sm:border-blue-950 sm:shadow-2xl">
        {view === "success" && loggedInEmployee ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-7 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Berhasil Masuk
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Selamat datang, {loggedInEmployee.nama}
              </p>
            </div>
 
            <div className="w-full space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Nama</span>
                <span className="font-medium text-slate-800">
                  {loggedInEmployee.nama}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Jabatan</span>
                <span className="font-medium text-slate-800">
                  {loggedInEmployee.jabatan ?? "-"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">No. Karyawan</span>
                <span className="font-mono text-slate-800">
                  {loggedInEmployee.no_kary ?? "-"}
                </span>
              </div>
            </div>
 
            <button
              type="button"
              // onClick={handleLogout}
              className="mt-2 text-sm font-medium text-slate-500 underline underline-offset-4 hover:text-slate-800"
            >
              Sedang Login . . . .
            </button>
          </div>
        ) : (
          // ------------------------------------------------------------
          // Login form
          // ------------------------------------------------------------
          <div className="flex flex-1 flex-col justify-center px-7 py-10">
            {/* Branding */}
            <div className="mb-10 flex flex-col items-center text-center">
              {/* <Logo className="h-16 w-16" /> */}
              <img src={logo} alt="Logo" className="h-16 w-16 w-auto object-contain center" />
              <p className="mt-4 text-sm font-bold uppercase tracking-widest text-blue-950">
                SiPasi
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Sistem Kepagawaian Absensi
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Bawaslu Pohuwato
              </p>
            </div>
 
            {/* Error banner */}
            {submitState === "error" && errorMessage && (
              <div className="mb-4 flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{errorMessage}</p>
              </div>
            )}
 
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="no_kary"
                  className="mb-1.5 block text-xs font-medium text-slate-600"
                >
                  Nomor Karyawan
                </label>
                <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 focus-within:border-blue-950">
                  <BadgeCheck className="h-4 w-4 shrink-0 text-slate-400" />
                  <input
                    id="no_kary"
                    type="text"
                    autoComplete="username"
                    placeholder="EMP-0231"
                    value={noKary}
                    onChange={(e) => { setNoKary(e.target.value); if (submitState === 'error') { setErrorMessage(null); setSubmitState('idle'); } }}
                    disabled={submitState === 'loading'}
                    className="w-full bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
                  />
                </div>
              </div>
 
              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-xs font-medium text-slate-600"
                >
                  Kata Sandi
                </label>
                <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 focus-within:border-blue-950">
                  <Lock className="h-4 w-4 shrink-0 text-slate-400" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Masukkan kata sandi"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); if (submitState === 'error') { setErrorMessage(null); setSubmitState('idle'); } }}
                    disabled={submitState === 'loading'}
                    className="w-full bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="shrink-0 text-slate-400 hover:text-slate-600"
                    aria-label={
                      showPassword
                        ? "Sembunyikan kata sandi"
                        : "Tampilkan kata sandi"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
 
              <button
                type="submit"
                disabled={!canSubmit}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-950 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-blue-900 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                {submitState === "loading" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Memproses...
                  </>
                ) : (
                  "Masuk"
                )}
              </button>
            </form>
 
            <p className="mt-6 text-center text-[11px] text-slate-400">
              Hubungi admin/HR jika lupa kata sandi atau mengalami kendala
              masuk.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}